import { useSearchParams } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useMe } from "@/api/hooks/useAuth";
import { PracaSection } from "@/components/sprava/PracaSection";
import { PouzivateliaSection } from "@/components/nastavenia/PouzivateliaSection";
import { ZalohySection } from "@/components/sprava/ZalohySection";

type SpravaTab = "cas" | "pouzivatelia" | "zalohy";

const TAB_LABELS: Record<SpravaTab, string> = {
  cas: "Odpracovaný čas",
  pouzivatelia: "Používatelia",
  zalohy: "Zálohy",
};

const TABY = Object.keys(TAB_LABELS) as SpravaTab[];

/**
 * Správa — veci, ktoré patria adminovi.
 *
 * Do KOLA 69 to boli dva taby v Histórii. Lenže história má byť prístupná
 * **všetkým** (kto čo zmenil na zákazke je bežná pracovná informácia),
 * kým odpracovaný čas a účty sú vec vedenia. Jedna stránka teda miešala
 * dve úrovne prístupu.
 *
 * **Tab žije v URL** (`?tab=`), nie v `localStorage` — konkrétny výber sa
 * dá poslať odkazom; neznámy tab padne do defaultu.
 *
 * Skutočná ochrana je v backende (`require_admin_when_enabled` na
 * `/api/praca`, `require_admin` na `/api/users`); toto je len slušnosť,
 * aby sa nie-admin nepozeral na prázdnu stránku s chybami.
 *
 * Pri **vypnutej** autentifikácii sa stránka vykreslí — `PouzivateliaSection`
 * tam vypisuje presný návod, čo treba na serveri prepnúť. Skryť ju by
 * znamenalo, že sa admin nikdy nedozvie, prečo login nefunguje.
 */
export function SpravaPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const { data: meData } = useMe();
  const me = meData?.data;
  // Kým nevieme, kto sme, nič sa nerenderuje: inak by tab Odpracovaný čas
  // stihol vystreliť dotaz a bežný zamestnanec by dostal 403 skôr, než sa
  // stránka rozhodne, že ju vôbec nemá vidieť
  const nevieme = meData === undefined;
  const zakazany = me?.auth_enabled === true && me.user?.je_admin !== true;

  const tab = (
    TABY.includes(params.get("tab") as SpravaTab) ? params.get("tab") : "cas"
  ) as SpravaTab;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-3xl">
          <ShieldCheck className="h-7 w-7 text-primary" />
          Správa
        </h1>
        <p className="text-sm text-muted-foreground">
          Koľko sa na zákazkách odpracovalo a kto má do programu prístup.
        </p>
      </div>

      {nevieme ? (
        <Skeleton className="h-24 w-full" />
      ) : zakazany ? (
        <p className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
          Táto sekcia je len pre administrátora.
        </p>
      ) : (
        <>
          <Tabs
            value={tab}
            onValueChange={(v) => {
              const next = new URLSearchParams(params);
              next.set("tab", v);
              setParams(next, { replace: true });
            }}
          >
            <TabsList>
              {TABY.map((t) => (
                <TabsTrigger key={t} value={t}>
                  {TAB_LABELS[t]}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {/* Vetvy sa renderujú výhradne, nie skryté cez CSS — inak by
              tab Používatelia zbytočne ťahal prehľad odpracovaného času.
              Od tretieho tabu je to výber podľa hodnoty, nie vnorené
              ternáre. */}
          {
            {
              cas: <PracaSection />,
              pouzivatelia: <PouzivateliaSection />,
              zalohy: <ZalohySection />,
            }[tab]
          }
        </>
      )}
    </div>
  );
}
